import * as yup from 'yup';
import {
    emailField,
    INVITED_NAME_MAX,
    optionalText,
    phoneNumber,
    requiredText,
    TranslateFn,
} from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/** The length `appointments.notes` holds, and the API's own cap on the field. */
export const NOTE_MAX: number = 2000;

/** The API refuses a phone number past this: `users.phone_number` is VARCHAR(30). */
export const PHONE_MAX: number = 30;

export interface PublicBookingChoiceValues {
    serviceId: string;
    therapistId: string;
    slotStartsAt: string;
    note: string;
}

/** What is chosen before anybody says who they are. */
export const createPublicBookingChoiceSchema = (translate: TranslateFn) => yup.object({
    serviceId: yup.string().required(),
    therapistId: yup.string().required(),
    slotStartsAt: yup.string().required(),
    note: optionalText(translate, NOTE_MAX),
});

export interface SignupValues {
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    gdprConsent: boolean;
    termsAccepted: boolean;
    website: string;
}

/**
 * Who is asking. No password: that is chosen on the page the emailed link opens, so only whoever
 * reads that inbox can ever set one.
 *
 * Names are capped at 50, not `personName`'s 100, because they become `users.first_name` and
 * `users.last_name` when the link is followed -- the same cap invitations use. A name the account
 * cannot hold would pass here and fail at the very end, after the email round trip.
 */
export const createSignupSchema = (translate: TranslateFn) => yup.object({
    emailAddress: emailField(translate),
    firstName: requiredText(translate, ResourcesIds.FIRST_NAME_REQUIRED, INVITED_NAME_MAX),
    lastName: requiredText(translate, ResourcesIds.LAST_NAME_REQUIRED, INVITED_NAME_MAX),
    phoneNumber: phoneNumber(translate, ResourcesIds.PHONE_NUMBER_INVALID, true)
        .max(PHONE_MAX, translate(ResourcesIds.CLINIC_FIELD_TOO_LONG, { max: PHONE_MAX })),
    gdprConsent: yup.boolean().defined()
        .oneOf([true], translate(ResourcesIds.EVALUATION_GDPR_CONSENT_REQUIRED)),
    termsAccepted: yup.boolean().defined()
        .oneOf([true], translate(ResourcesIds.TERMS_AND_CONDITIONS_REQUIRED)),
    // The honeypot: in the schema so the form carries it, and deliberately unvalidated -- a
    // bot filling it in must be sent on its way looking accepted, not told why it failed.
    website: yup.string().defined(),
});
